import { roundToAvailableLoad } from '../gym/round-load.js';
import { roundKg } from '../units/round.js';

/**
 * Series de aproximacion (docs/TRAINING_ENGINE.md §7.3).
 *
 * Se muestran y pueden marcarse como hechas, pero NO cuentan como series
 * efectivas ni entran en el volumen: viven en una tabla aparte, de modo que es
 * estructuralmente imposible que contaminen el computo (ADR-013).
 */
export interface WarmupSet {
  orderIndex: number;
  loadKg: number;
  reps: number;
  restSeconds: number;
}

export interface WarmupInput {
  workLoadKg: number | null;
  e1rmKg: number | null;
  /** Primer ejercicio de la sesion. */
  isFirstOfSession: boolean;
  /** El musculo ya se ha trabajado en esta sesion. */
  muscleAlreadyWarm: boolean;
  isCompound: boolean;
  availableLoads: readonly number[];
}

interface Step {
  fraction: number;
  reps: number;
}

const FULL: Step[] = [
  { fraction: 0.4, reps: 8 },
  { fraction: 0.6, reps: 5 },
  { fraction: 0.8, reps: 3 },
];
const SHORT: Step[] = [
  { fraction: 0.6, reps: 5 },
  { fraction: 0.8, reps: 3 },
];
const MINIMAL: Step[] = [{ fraction: 0.6, reps: 6 }];

export function generateWarmup(input: WarmupInput): WarmupSet[] {
  const work = input.workLoadKg;
  if (work === null || work <= 0) return [];

  // Con carga baja respecto al maximo estimado, calentar no aporta.
  const relative = input.e1rmKg !== null && input.e1rmKg > 0 ? work / input.e1rmKg : null;
  if (relative !== null && relative < 0.5) return [];
  if (!input.isCompound && input.muscleAlreadyWarm) return [];

  let steps: Step[];
  if (input.isFirstOfSession && (relative === null || relative >= 0.6)) {
    steps = FULL;
  } else if (input.isCompound) {
    steps = input.muscleAlreadyWarm ? SHORT : FULL;
  } else {
    steps = MINIMAL;
  }

  const sets: WarmupSet[] = [];
  let previous = 0;

  for (const step of steps) {
    const rawLoad = work * step.fraction;
    const rounded =
      input.availableLoads.length > 0
        ? roundToAvailableLoad(rawLoad, input.availableLoads).loadKg
        : roundKg(rawLoad);

    if (rounded === null || rounded <= 0) continue;
    // Sin repetir carga ni alcanzar la de trabajo.
    if (rounded <= previous || rounded >= work) continue;

    previous = rounded;
    sets.push({
      // La posicion en la LISTA EMITIDA, no en la plantilla. Cuando un escalon
      // se descarta —porque redondea a la misma carga que el anterior o porque
      // alcanza la de trabajo— numerar por la plantilla dejaba huecos: con
      // cargas gruesas salia [1, 3], y el usuario veia «Aproximacion 1» y
      // «Aproximacion 3» sin que faltara ninguna.
      orderIndex: sets.length + 1,
      loadKg: rounded,
      reps: step.reps,
      restSeconds: 45,
    });
  }

  return sets;
}
